import {
  CATEGORY_NON_RESIDENT_PREMIUM,
  CATEGORY_NON_RESIDENT_STANDARD,
  CATEGORY_NON_RESIDENT_TOTAL_SEATS,
  CATEGORY_PREMIUM,
  CATEGORY_STANDARD,
} from 'data/Pilgrims';

export const formatNumber = (value) => new Intl.NumberFormat('fr-FR').format(value);

const validCounts = (value) =>
  value &&
  ['total', 'occupied', 'available'].every((key) => Number.isInteger(value[key]) && value[key] >= 0) &&
  value.available === Math.max(value.total - value.occupied, 0);

const quotaSignature = (quota) =>
  JSON.stringify({
    campaign_year: quota.campaign_year,
    national: quota.national,
    groups: [...quota.groups]
      .map((group) => ({ ...group, category_ids: [...group.category_ids].sort() }))
      .sort((a, b) => a.id.localeCompare(b.id)),
  });

const exactlyOne = (items) => (items.length === 1 ? items[0] : null);

const publicCategoryDefinitions = [
  {
    name: CATEGORY_STANDARD,
    label: 'Standard',
    audience: 'Résidents',
    tier: 'Standard',
    poolKey: 'standard',
  },
  {
    name: CATEGORY_PREMIUM,
    label: 'Premium',
    audience: 'Résidents',
    tier: 'Premium',
    poolKey: 'premium',
  },
  {
    name: CATEGORY_NON_RESIDENT_STANDARD,
    label: 'Non-résident Standard',
    audience: 'Non-résidents',
    tier: 'Standard',
    poolKey: 'nonResident',
  },
  {
    name: CATEGORY_NON_RESIDENT_PREMIUM,
    label: 'Non-résident Premium',
    audience: 'Non-résidents',
    tier: 'Premium',
    poolKey: 'nonResident',
  },
];

const makePool = (group, label) => ({
  id: group.id,
  label,
  total: group.total,
  occupied: group.occupied,
  available: group.available,
  shared: false,
});

const buildPublicLandingView = (categories, quota) => {
  if (!categories.every((category) => category && typeof category.id === 'string' && category.id.length > 0)) {
    throw new Error('Incomplete public landing data');
  }
  const ids = categories.map((category) => category.id);
  const publicCategories = publicCategoryDefinitions.map((definition) => {
    const category = exactlyOne(categories.filter((item) => item.name === definition.name));
    if (
      !category ||
      !category.id ||
      !Number.isInteger(category.price?.public) ||
      category.price.public < 0 ||
      !Number.isInteger(category.occupied_seats) ||
      category.occupied_seats < 0
    ) {
      throw new Error('Incomplete public landing data');
    }
    return { ...category, ...definition };
  });
  if (
    new Set(ids).size !== ids.length ||
    new Set(publicCategories.map((category) => category.id)).size !== publicCategoryDefinitions.length
  ) {
    throw new Error('Incomplete public landing data');
  }

  const publicCategoryByName = Object.fromEntries(publicCategories.map((category) => [category.name, category]));
  const standardCategory = publicCategoryByName[CATEGORY_STANDARD];
  const premiumCategory = publicCategoryByName[CATEGORY_PREMIUM];
  const nonResidentStandard = publicCategoryByName[CATEGORY_NON_RESIDENT_STANDARD];
  const nonResidentPremium = publicCategoryByName[CATEGORY_NON_RESIDENT_PREMIUM];
  const findOrdinaryPool = (category) =>
    exactlyOne(
      quota.groups.filter(
        (group) => !group.shared && group.category_ids.length === 1 && group.category_ids[0] === category.id
      )
    );
  const standardGroup = findOrdinaryPool(standardCategory);
  const premiumGroup = findOrdinaryPool(premiumCategory);
  const nonResidentIds = [nonResidentStandard.id, nonResidentPremium.id].sort();
  const nonResidentGroup = exactlyOne(
    quota.groups.filter(
      (group) =>
        group.shared &&
        group.category_ids.length === 2 &&
        [...group.category_ids].sort().every((id, index) => id === nonResidentIds[index])
    )
  );
  const nonResidentOccupied = nonResidentStandard.occupied_seats + nonResidentPremium.occupied_seats;
  if (
    !standardGroup ||
    !premiumGroup ||
    !nonResidentGroup ||
    standardGroup.occupied !== standardCategory.occupied_seats ||
    premiumGroup.occupied !== premiumCategory.occupied_seats ||
    nonResidentGroup.occupied !== nonResidentOccupied
  ) {
    throw new Error('Incomplete public landing data');
  }

  const standard = makePool(standardGroup, 'Standard');
  const premium = makePool(premiumGroup, 'Premium');
  const nonResident = {
    id: nonResidentGroup.id,
    label: 'Non-résidents',
    total: CATEGORY_NON_RESIDENT_TOTAL_SEATS,
    occupied: nonResidentOccupied,
    available: Math.max(CATEGORY_NON_RESIDENT_TOTAL_SEATS - nonResidentOccupied, 0),
    shared: true,
    segments: [
      { label: 'Non-résident Standard', occupied: nonResidentStandard.occupied_seats },
      { label: 'Non-résident Premium', occupied: nonResidentPremium.occupied_seats },
    ],
  };
  const publicQuota = { national: quota.national, standard, premium, nonResident };
  const pools = [publicQuota.standard, publicQuota.premium, publicQuota.nonResident];
  return {
    publicQuota: { ...publicQuota, pools },
    publicCategories: publicCategories.map((category) => ({
      ...category,
      quota: publicQuota[category.poolKey],
    })),
  };
};

export const loadLandingData = async (api) => {
  const preferences = await api.retrieve('preference', 'public/')();
  const pricingYears = [...(preferences?.hajj_pricing_information || '').matchAll(/Hadj\s+(\d{4})/gi)];
  const pricingInformation = pricingYears.some((match) => Number(match[1]) !== preferences.campaign_year)
    ? ''
    : preferences?.hajj_pricing_information;
  const categories = [];
  const cursors = new Set();
  let cursor;
  let quota;
  let stableQuotaSignature;
  let expectedCount;
  let page;
  do {
    const params = { limit: 250, ...(cursor ? { cursor } : {}) };
    ({ data: page } = await api.get('category/', { params }));
    if (
      page?.object !== 'list' ||
      !Array.isArray(page.data) ||
      !page.quota ||
      !Number.isInteger(page.count) ||
      page.count < 0 ||
      !Number.isFinite(Date.parse(page.quota.generated_at)) ||
      !validCounts(page.quota.national) ||
      !Array.isArray(page.quota.groups) ||
      !page.quota.groups.every(
        (group) =>
          group &&
          typeof group.id === 'string' &&
          validCounts(group) &&
          Array.isArray(group.category_ids) &&
          group.category_ids.every((id) => typeof id === 'string')
      ) ||
      new Set(page.quota.groups.map((group) => group.id)).size !== page.quota.groups.length
    ) {
      throw new Error('Public quota contract unavailable');
    }
    const pageQuotaSignature = quotaSignature(page.quota);
    if (stableQuotaSignature && stableQuotaSignature !== pageQuotaSignature) {
      throw new Error('Campaign quota changed during pagination');
    }
    if (!quota) {
      quota = page.quota;
      stableQuotaSignature = pageQuotaSignature;
      expectedCount = page.count;
    } else if (page.count !== expectedCount) {
      throw new Error('Category count changed during pagination');
    }
    categories.push(...page.data);
    cursor = page.next ? new URL(page.next, 'http://localhost').searchParams.get('cursor') : null;
    if (page.next && (!cursor || cursors.has(cursor))) throw new Error('Incomplete category pagination');
    if (cursor) cursors.add(cursor);
  } while (cursor);

  if (
    categories.length !== expectedCount ||
    !Number.isInteger(preferences?.campaign_year) ||
    preferences.campaign_year !== quota.campaign_year ||
    !Number.isFinite(Date.parse(quota.generated_at))
  ) {
    throw new Error('Incomplete public landing data');
  }
  const view = buildPublicLandingView(categories, quota);
  return { preferences: { ...preferences, hajj_pricing_information: pricingInformation }, categories, quota, ...view };
};
